import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Chip,
  Paper,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  CircularProgress,
  Menu,
  Divider,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useToast } from '../../hooks/useToast';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateFormFieldsSx,
} from '../../../theme/harmonateStyles';

const TreasuryFileUpload = ({ onClose, onUploadComplete }) => {
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    tenantId: localStorage.getItem('tenant_id'),
    unitOfWorkName: 'Treasury Upload',
    documentType: '',
    fundId: '',
    investorId: '',
    holdingId: '',
    systemId: '',
  });
  
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadResults, setUploadResults] = useState([]);
  const [errors, setErrors] = useState([]);
  const [documentTypes, setDocumentTypes] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  
  // State for drag and drop
  const [isDragging, setIsDragging] = useState(false);
  
  // State for API document types (for Excel files)
  const [apiDocumentTypes, setApiDocumentTypes] = useState([]);
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(false);
  
  // State for source general ledger
  const [systems, setSystems] = useState([]);
  const [loadingSystems, setLoadingSystems] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState('');
  
  // Initialize toast
  const { showSuccess, showError } = useToast();
  
  const getAuthToken = () => {
    return localStorage.getItem('token');
  };

  // Fetch document types from API
  const fetchDocumentTypes = async () => {
    try {
      setLoadingDocumentTypes(true);
      const token = getAuthToken();
      const category = 'TR'; // Changed from 'REF' to 'TR' for Treasury
      const url = new URL(`${process.env.REACT_APP_BACKEND_URL}conductor/document-types/by-category/${category}`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch document types: ${response.status}`);
      }

      const data = await response.json();
      setApiDocumentTypes(data);
      
      // Create a mapping of document type names to their IDs
      const typesMap = {};
      data.forEach(type => {
        typesMap[type.name] = type.id;
      });
      setDocumentTypes(typesMap);
      
      // If there's only one document type, select it by default
      if (data.length === 1) {
        setFormData(prev => ({
          ...prev,
          documentType: data[0].name
        }));
      }
      
    } catch (error) {
      console.error('Error fetching document types:', error);
      setErrors(prev => [...prev, {
        id: Date.now(),
        message: 'Failed to load document types. Please refresh the page to try again.',
        type: 'error'
      }]);
    } finally {
      setLoadingDocumentTypes(false);
    }
  };

  // Load document types and systems on component mount
  useEffect(() => {
    fetchDocumentTypes();
    fetchSystems();
  }, []);

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files || event.dataTransfer?.files || []);
    
    if (newFiles.length === 0) return;
    
    // Filter out files that are already in the list
    const uniqueNewFiles = newFiles.filter(newFile => 
      !files.some(existingFile => 
        existingFile.name === newFile.name && 
        existingFile.size === newFile.size
      )
    );
    
    if (uniqueNewFiles.length === 0) {
      setErrors(prev => [...prev, {
        id: Date.now(),
        message: 'Some files were already added',
        type: 'warning'
      }]);
      return;
    }
    
    // Validate file types
    const validFiles = uniqueNewFiles.filter(file => {
      const fileName = file.name.toLowerCase();
      const isValid = fileName.endsWith('.xlsx') || 
                     fileName.endsWith('.xls') || 
                     file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                     file.type === 'application/vnd.ms-excel';
      
      if (!isValid) {
        setErrors(prev => [...prev, {
          id: Date.now(),
          message: `Unsupported file type: ${file.name}. Only Excel files are supported.`,
          type: 'error'
        }]);
        return false;
      }
      return true;
    });
    
    setFiles(prev => [...prev, ...validFiles]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone
    if (e.currentTarget === e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange(e);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setUploadProgress(prev => {
      const newProgress = {...prev};
      delete newProgress[index];
      return newProgress;
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when field is edited
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle system selection change
  const handleSystemChange = (event) => {
    setSelectedSystem(event.target.value);
    setFormData(prev => ({
      ...prev,
      systemId: event.target.value
    }));
    
    // Clear validation error when field is edited
    if (validationErrors.systemId) {
      setValidationErrors(prev => ({
        ...prev,
        systemId: null
      }));
    }
  };

  // Fetch systems for Source General Ledger
  const fetchSystems = async () => {
    setLoadingSystems(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/systems`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch systems: ${response.status}`);
      }
      
      const data = await response.json();
      setSystems(data);
      
      // If there's only one system, select it by default
      if (data.length === 1) {
        setSelectedSystem(data[0].id);
        setFormData(prev => ({
          ...prev,
          systemId: data[0].id
        }));
      }
    } catch (error) {
      console.error('Error fetching systems:', error);
      setErrors(prev => [...prev, {
        id: Date.now(),
        message: 'Failed to load systems. Some features may be limited.',
        type: 'warning'
      }]);
    } finally {
      setLoadingSystems(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.documentType) {
      newErrors.documentType = 'Document type is required';
    }
    
    if (!formData.systemId) {
      newErrors.systemId = 'Source General Ledger is required';
    }
    
    if (files.length === 0) {
      newErrors.files = 'Please select at least one file to upload';
    }
    
    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Constants for multi-part upload
  const PART_SIZE = 5 * 1024 * 1024; // 5MB chunks
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  // Helper function to create file metadata
  const createFileMeta = (file) => {
    const userId = localStorage.getItem('user_id');
    const system = systems.find(s => s.id === formData.systemId);
    const sourceGeneralLedger = system ? system.name.toUpperCase().replace(/\s+/g, '_') : null;
    
    return {
      name: file.name,
      content_type: file.type,
      user_id: userId,
      document_type_id: documentTypes[formData.documentType],
      source_general_ledger: sourceGeneralLedger,
      fund_id: formData.fundId || null,
      investor_id: formData.investorId || null,
      holding_id: formData.holdingId || null,
      system_id: formData.systemId || null
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setUploading(true);
    setErrors([]);
    const results = [];
    const token = getAuthToken();
    const tenantId = localStorage.getItem('tenant_id');
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileMeta = createFileMeta(file);
        
        try {
          // 1. Create unit of work
          const uowResponse = await fetch(`${API_BASE}api/${tenantId}/units-of-work`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
              name: formData.unitOfWorkName, 
              documents: [fileMeta]
            })
          });
          
          if (!uowResponse.ok) {
            throw new Error(`Failed to create unit of work: ${uowResponse.status}`);
          }
          
          const uowData = await uowResponse.json();
          const fileData = uowData.documents[0]; // Get the first document
          
          // 2. Get pre-signed URLs for each part
          const totalParts = Math.ceil(file.size / PART_SIZE);
          const urlsResponse = await fetch(`${API_BASE}api/multipart/urls`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              id: fileData.id,
              tenant_id: tenantId,
              unit_of_work_id: uowData.id,
              location: fileData.location, // Use the location from the unit of work response
              upload_id: fileData.upload_id, // Include upload_id if present
              name: file.name,
              content_type: file.type,
              total_parts: totalParts
            })
          });
          
          if (!urlsResponse.ok) {
            throw new Error(`Failed to get upload URLs: ${urlsResponse.status}`);
          }
          
          const urlsData = await urlsResponse.json();
          
          // 3. Upload each part
          const parts = [];
          for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
            const start = (partNumber - 1) * PART_SIZE;
            const end = Math.min(start + PART_SIZE, file.size);
            const partBlob = file.slice(start, end);
            const urlObj = urlsData.urls.find(p => p.part_number === partNumber);
            
            if (!urlObj) {
              throw new Error(`Missing URL for part ${partNumber}`);
            }
            
            const uploadResponse = await fetch(urlObj.url, {
              method: 'PUT',
              body: partBlob
            });
            
            if (!uploadResponse.ok) {
              throw new Error(`Failed to upload part ${partNumber}`);
            }
            
            const etag = uploadResponse.headers.get('ETag')?.replace(/"/g, '');
            parts.push({ etag, part_number: partNumber });
            
            // Update progress
            const progress = Math.round((partNumber / totalParts) * 100);
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: progress
            }));
          }
          
          // 4. Complete the multi-part upload
          const completeResponse = await fetch(`${API_BASE}api/multipart/complete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              id: fileData.id,
              unit_of_work_id: uowData.id,
              location: fileData.location, // Include location from unit of work
              upload_id: fileData.upload_id, // Include upload_id from unit of work
              name: file.name,
              content_type: file.type,
              parts
            })
          });
          
          if (!completeResponse.ok) {
            throw new Error('Failed to complete upload');
          }
          
          const completeData = await completeResponse.json();
          
          // Add success result
          results.push({
            success: true,
            file: file.name,
            data: completeData,
            workflowInstanceId: completeData.workflow_instance_id
          });
          
          setUploadResults([...results]);
          
        } catch (fileError) {
          console.error(`Error uploading ${file.name}:`, fileError);
          results.push({
            success: false,
            file: file.name,
            error: fileError.message
          });
          setUploadResults([...results]);
        }
      }
      
      // Handle final results
      const allSuccessful = results.every(r => r.success);
      
      if (allSuccessful) {
        showSuccess(`Successfully uploaded ${results.length} file(s)`, 'Upload Complete');
        if (onUploadComplete) {
          onUploadComplete(results);
        }
        setTimeout(() => onClose(), 1500);
      } else {
        const failedUploads = results.filter(r => !r.success);
        const errorMessages = failedUploads.map(upload => 
          `• ${upload.file}: ${upload.error}`
        ).join('\n');
        
        setErrors(prev => [{
          id: Date.now(),
          message: `Failed to upload ${failedUploads.length} file(s):\n${errorMessages}`,
          type: 'error'
        }]);
      }
      
    } catch (error) {
      console.error('Upload process error:', error);
      setErrors(prev => [{
        id: Date.now(),
        message: `Upload failed: ${error.message}`,
        type: 'error'
      }]);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ p: 3, ...harmonateFormFieldsSx }}>
      
      {/* Error messages */}
      {errors.map(error => (
        <Alert 
          key={error.id} 
          severity={error.type === 'error' ? 'error' : 'warning'}
          sx={{ mb: 2 }}
          onClose={() => setErrors(prev => prev.filter(e => e.id !== error.id))}
        >
          {error.message}
        </Alert>
      ))}
      
      {/* File selection */}
      <Paper 
        variant="outlined" 
        component="div"
        sx={{ 
          p: 6,
          mb: 4,
          minHeight: '200px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          border: '2px dashed',
          borderColor: isDragging ? ht.green : ht.blue,
          backgroundColor: isDragging ? ht.secondaryTab : ht.listBg,
          borderRadius: ht.cardRadius,
          '&:hover': {
            borderColor: ht.green,
            backgroundColor: ht.secondaryTab,
            transform: 'scale(1.005)'
          },
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          textAlign: 'center',
          boxShadow: ht.cardShadow,
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept=".xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          multiple
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CloudUploadIcon sx={{ fontSize: 60, mb: 2, color: isDragging ? ht.green : ht.blue }} />
          <Typography variant="h6" color="textSecondary" sx={{ fontWeight: isDragging ? 600 : 400, mb: 1 }}>
            {isDragging ? 'Drop files here' : 'Click to select files or drag and drop'}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1.5 }}>
            Only Excel files are supported (.xlsx, .xls)
          </Typography>
        </Box>
      </Paper>
      
      {/* Selected files */}
      {files.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Files ({files.length}):
          </Typography>
          {files.map((file, index) => (
            <Paper 
              key={`${file.name}-${file.size}`}
              sx={{ 
                p: 1.5, 
                mb: 1, 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: ht.fieldRadius,
                borderColor: ht.line,
              }}
              variant="outlined"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <FileIcon sx={{ mr: 1.5, color: ht.text }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap variant="body2">
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
              </Box>
              
              {uploadProgress[index] !== undefined ? (
                <Box sx={{ width: '100px', ml: 2 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress[index] || 0} 
                    sx={{
                      height: 8,
                      borderRadius: 5,
                      bgcolor: ht.line,
                      '& .MuiLinearProgress-bar': { bgcolor: ht.green },
                    }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    {Math.round(uploadProgress[index] || 0)}%
                  </Typography>
                </Box>
              ) : (
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}
                  sx={{ color: ht.text }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Paper>
          ))}
          
          {validationErrors.files && (
            <Typography color="error" variant="caption">
              {validationErrors.files}
            </Typography>
          )}
        </Box>
      )}
      
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        {/* Document type selection */}
        <FormControl fullWidth error={!!validationErrors.documentType} sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Document Type *
          </Typography>
          {loadingDocumentTypes ? (
            <CircularProgress size={24} sx={{ color: ht.blue }} />
          ) : (
            <Select
              name="documentType"
              value={formData.documentType}
              onChange={handleInputChange}
              displayEmpty
              fullWidth
              size="small"
              error={!!validationErrors.documentType}
            >
              <MenuItem value="" disabled>
                Select document type
              </MenuItem>
              {apiDocumentTypes.map((type) => (
                <MenuItem key={type.id} value={type.name}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          )}
          {validationErrors.documentType && (
            <Typography color="error" variant="caption">
              {validationErrors.documentType}
            </Typography>
          )}
        </FormControl>

        {/* Source General Ledger (System) Selection */}
        <FormControl fullWidth error={!!validationErrors.systemId} sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Source General Ledger *
          </Typography>
          {loadingSystems ? (
            <CircularProgress size={24} sx={{ color: ht.blue }} />
          ) : (
            <Select
              value={selectedSystem}
              onChange={handleSystemChange}
              displayEmpty
              fullWidth
              size="small"
              error={!!validationErrors.systemId}
            >
              <MenuItem value="" disabled>
                {systems.length === 0 ? 'No systems available' : 'Select system'}
              </MenuItem>
              {systems.map((system) => (
                <MenuItem key={system.id} value={system.id}>
                  {system.name}
                </MenuItem>
              ))}
            </Select>
          )}
          {validationErrors.systemId && (
            <Typography color="error" variant="caption">
              {validationErrors.systemId}
            </Typography>
          )}
        </FormControl>
      </Box>
      
      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button 
          variant="outlined" 
          onClick={onClose}
          disabled={uploading}
          sx={harmonateCancelButtonSx}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={uploading || files.length === 0 || !formData.documentType || !formData.systemId}
          startIcon={uploading ? <CircularProgress size={20} sx={{ color: ht.white }} /> : null}
          sx={harmonateProceedButtonSx}
        >
          {uploading ? 'Uploading...' : 'Upload Files'}
        </Button>
      </Box>
      
      {/* Upload results */}
      {uploadResults.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Upload Results:
          </Typography>
          {uploadResults.map((result, index) => (
            <Box 
              key={index} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1,
                color: result.success ? ht.green : ht.red
              }}
            >
              {result.success ? (
                <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} />
              ) : (
                <ErrorIcon fontSize="small" sx={{ mr: 1 }} />
              )}
              <Typography variant="body2">
                {result.file}: {result.success ? 'Uploaded successfully' : result.error}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TreasuryFileUpload;